/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */

import {
  ImageHelper,
  RouteHelper,
  calcProgress,
  CellType,
  useIsLoggedIn,
} from "@nf/common";
import { TILE_OUT_OF_VIEW_WIDTH } from "@nf/constants";
import React, { useMemo, useRef, useState } from "react";

import dynamic from "next/dynamic";

const ProgressBar = dynamic(() => import("components/ProgressBar"), {
  ssr: false,
});

const ImageWithPlaceholder = dynamic(
  () => import("components/ImageWithPlaceholder")
);

const AvailabilityLabel = dynamic(() => import("components/AvailabilityLabel"));

import { useTileEntry } from "../../../../hooks";
import { isServerSideRender } from "@nf/helpers";
import { IListComponentItemProps } from "../../models";
import cx from "classnames";
import styles from "./ListComponentItemFrame.module.scss";
import ListComponentItemModal from "../ListComponentItemModal/ListComponentItemModal";
import { LinkWithLocale } from "components/LinkWithLocale";

export interface IListComponentItemFrameProps extends IListComponentItemProps {
  width: number;
}

export const ListComponentItemFrame = ({
  media,
  isPlaceholder = false,
  withProgress,
  gridColumns,
  query,
  cellType,
}: IListComponentItemFrameProps) => {
  const frameRef = useRef<HTMLAnchorElement>(null);
  const [isHover, setIsHover] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [isOutOfView, setIsOutOfView] = useState(false);
  let mouseEnterTimeout: NodeJS.Timeout;
  const isDesktopSize = !isServerSideRender && window.innerWidth > 850;
  const isLargeFrame = cellType === CellType.FrameLarge;
  const bannerImageUrl = ImageHelper.getBannerImageUrl(media?.Images);
  const isLoggedIn = useIsLoggedIn();
  const progress = calcProgress(media);

  const resize = useMemo(() => {
    if (gridColumns && gridColumns > 5) {
      return { width: 1.1, height: 1.333 };
    }

    if (gridColumns || isLargeFrame) {
      return { width: 0.62, height: 0.775 };
    }

    return { width: 0.785, height: 0.975 };
  }, []);

  const tileEntryParams = {
    isDesktopSize,
    isHover,
    gridColumns,
    frameRef,
    cellType,
    resize,
  };
  const tileEntry = useTileEntry(tileEntryParams);
  const mouseEnter = () => {
    if (!isDesktopSize) return;
    mouseEnterTimeout = setTimeout(() => {
      setIsFadingOut(false);
      setIsHover(true);
      const boundingRect = frameRef?.current?.getBoundingClientRect();
      const tileRight =
        boundingRect &&
        boundingRect.right >
          (window.innerWidth + TILE_OUT_OF_VIEW_WIDTH ||
            document.documentElement.clientWidth + TILE_OUT_OF_VIEW_WIDTH);
      const tileLeft = boundingRect && boundingRect.left < 0;
      if (!isLargeFrame && (tileRight || tileLeft)) {
        setIsOutOfView(true);
      } else {
        setIsOutOfView(false);
      }
    }, 200);
  };

  const mouseLeave = () => {
    if (!isDesktopSize) return;
    setIsFadingOut(true);
    setTimeout(() => {
      setIsHover(false);
      setIsFadingOut(false);
      setIsOutOfView(false);
    }, 350);
    clearTimeout(mouseEnterTimeout);
  };

  if (!media || isPlaceholder) {
    return (
      <div
        className={cx(
          styles.item,
          styles.skeleton,
          isLargeFrame && styles.largeFrame
        )}
      >
        <div className={styles.container} />
      </div>
    );
  }

  const isModalAvailable = isHover && !isOutOfView && isDesktopSize;

  return (
    <div
      className={cx(
        styles.item,
        isOutOfView && styles.isOutOfView,
        isLargeFrame && styles.largeFrame
      )}
      onMouseEnter={mouseEnter}
      onMouseLeave={mouseLeave}
    >
      <LinkWithLocale href={RouteHelper.goToDetails(media, query)}>
        <a aria-label={media.Title} className={styles.container} ref={frameRef}>
          <AvailabilityLabel media={media} />

          <ImageWithPlaceholder
            alt={media.Title}
            src={bannerImageUrl}
            className={styles.image}
          />

          {withProgress && !!progress && <ProgressBar progress={progress} />}
        </a>
      </LinkWithLocale>

      {tileEntry &&
        tileEntry.dimension &&
        tileEntry.dimension.width &&
        tileEntry.dimension.height &&
        tileEntry.position &&
        isModalAvailable && (
          <ListComponentItemModal
            media={media}
            isLoggedIn={isLoggedIn}
            gridColumns={gridColumns}
            cellType={cellType}
            isFadingOut={isFadingOut}
            query={query}
            width={tileEntry.dimension.width}
            height={tileEntry.dimension.height}
            rectTop={tileEntry.position.top}
            rectLeft={tileEntry.position.left}
            rectRight={tileEntry.position.right}
          />
        )}
    </div>
  );
};
